import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ArrowRight, Coffee, AlertTriangle, Check } from "lucide-react";
import { VbButton, VbLoading, VbError } from "@/components/vb";
import {
  useBuyer,
  useAnalysis,
  useQuestions,
  useTriggerAnalysis,
  vbKeys,
} from "@/hooks/vb/useBuyers";
import { extractVbError } from "./buyer-create/errors";
import { parseRunProgress } from "./run/runProgress";
import { RunProviders } from "./run/RunProviders";
import { RunActivityLog } from "./run/RunActivityLog";

/** VB-E · the dedicated run-progress screen. The "Run analysis" trigger lives on
 *  the buyer detail page (and the FAILED state below); this screen is where the
 *  user lands afterwards to watch the run advance per provider, leave and come
 *  back (it's resumable by URL), and route to the verdict when it finishes.
 *
 *  Branches on the analysis shadow status: PENDING/IN_PROGRESS show the live
 *  "Asking AI…" card (polled), DONE redirects straight to the verdict (no
 *  intermediate card), and FAILED shows the error + "Run again". When reached
 *  from the create wizard (`?new=1`) it shows the 3-step header (step 2 of 3). */
export function RunPage() {
  const params = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  // `?new=1` is set when the create-buyer wizard navigates here ("Run
  // simulation", step 2 of 3) - so we show the 3-step wizard header. A re-run
  // from the buyer detail page omits it and shows the breadcrumb instead.
  const isCreateFlow = searchParams.get("new") === "1";
  const isOnboardingFlow = searchParams.get("onboarding") === "1";
  const buyerId = Number(params.buyerId);
  const analysisId = Number(params.analysisId);
  const validIds =
    Number.isInteger(buyerId) && buyerId > 0 && Number.isInteger(analysisId) && analysisId > 0;

  const buyer = useBuyer(validIds ? buyerId : undefined);
  const analysis = useAnalysis(
    validIds ? buyerId : undefined,
    validIds ? analysisId : undefined,
    { poll: true },
  );
  const questions = useQuestions(validIds ? buyerId : undefined);

  const status = analysis.data?.status;

  // When the run reaches a terminal state, refresh the detail/overview caches
  // once so they're current when the user navigates back (same pattern as the
  // detail page). Ref-guarded so it fires at most once per mount.
  const invalidatedRef = useRef(false);
  useEffect(() => {
    if (!validIds) return;
    if ((status === "DONE" || status === "FAILED") && !invalidatedRef.current) {
      invalidatedRef.current = true;
      qc.invalidateQueries({ queryKey: vbKeys.buyer(buyerId) });
      qc.invalidateQueries({ queryKey: vbKeys.analyses(buyerId) });
    }
  }, [status, validIds, buyerId, qc]);

  // This route element is reused across `:analysisId` changes (e.g. "Run again"
  // navigates run A → run B without remounting RunPage), so reset the per-run
  // guard when the id changes. RunActivityLog is keyed on analysisId below.
  useEffect(() => {
    invalidatedRef.current = false;
  }, [analysisId]);

  // The run's terminal events (virtual_buyer_run_completed / _run_failed) are
  // emitted server-side on finalize (buyer/services/run_events.py) — a run that
  // finishes after the user leaves this screen still gets its event, which the
  // old client-side emit couldn't guarantee (AITS-545).

  // On completion, advance straight to the verdict - no intermediate "complete"
  // card or button (better UX; the run screen is only for in-flight + failed).
  // `replace` so Back doesn't bounce the user onto the finished run screen.
  useEffect(() => {
    if (status === "DONE") {
      // Carry the create-flow flag so the verdict (step 3 of 3) can show the
      // wizard header too when VB-F1 lands.
      const query = new URLSearchParams();
      if (isCreateFlow) query.set("new", "1");
      if (isOnboardingFlow) query.set("onboarding", "1");
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      navigate(`/virtual-buyer/${buyerId}/verdict/${analysisId}${suffix}`, { replace: true });
    }
  }, [status, buyerId, analysisId, navigate, isCreateFlow]);

  // Move focus to the FAILED heading when a run fails: the running card
  // unmounts, so without this focus falls to <body> and the state change isn't
  // announced to assistive tech. (DONE redirects away, so only FAILED applies.)
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (status === "FAILED") headingRef.current?.focus();
  }, [status]);

  // The create-flow steps row scrolls horizontally on narrow screens; center the
  // ACTIVE step on mount so step 2 leads (otherwise it scrolls from the start and
  // the active step is clipped, making it look like you're still on step 1).
  // Declared here (before the early returns) so the hook order stays stable.
  const scrollActiveIntoView = useCallback((node: HTMLSpanElement | null) => {
    node?.scrollIntoView({ inline: "center", block: "nearest" });
  }, []);

  // "Run again" off the FAILED state - mirrors the detail page's runAnalysis
  // (409 → deep-link the already-running analysis instead of erroring).
  const triggerM = useTriggerAnalysis();
  const [runError, setRunError] = useState<string | null>(null);
  const runAgain = async () => {
    if (triggerM.isPending) return;
    setRunError(null);
    try {
      const shadow = await triggerM.mutateAsync({
        buyerId,
        questionsCount: questions.data?.length,
      });
      const query = new URLSearchParams();
      if (isCreateFlow) query.set("new", "1");
      if (isOnboardingFlow) query.set("onboarding", "1");
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      navigate(`/virtual-buyer/${buyerId}/run/${shadow.id}${suffix}`);
    } catch (err) {
      const e = extractVbError(err);
      if (e.status === 409) {
        const existingId = (err as { response?: { data?: { existingAnalysisId?: number } } })
          .response?.data?.existingAnalysisId;
        if (existingId != null) {
          const query = new URLSearchParams();
          if (isCreateFlow) query.set("new", "1");
          if (isOnboardingFlow) query.set("onboarding", "1");
          const suffix = query.size > 0 ? `?${query.toString()}` : "";
          navigate(`/virtual-buyer/${buyerId}/run/${existingId}${suffix}`);
          return;
        }
      }
      setRunError(e.detail);
    }
  };

  if (!validIds || analysis.isError) {
    // Distinguish a genuinely-missing run (404 / bad link) from a transient
    // reachability problem (throttle, upstream blip, network) so we don't tell
    // the user their run "may have been removed" when it's just a hiccup.
    const err = analysis.isError ? extractVbError(analysis.error) : null;
    const transient =
      err != null && (err.status == null || err.status === 429 || err.status >= 500);
    return (
      <VbError
        title={transient ? "Couldn't reach the run" : "Run not found"}
        description={
          transient
            ? "We're having trouble reaching the analysis service. This is usually temporary - try again in a moment."
            : "We couldn't load this analysis. It may have been removed, or the link is wrong."
        }
        onRetry={validIds ? () => analysis.refetch() : undefined}
      />
    );
  }
  if (analysis.isPending) {
    return <VbLoading rows={6} />;
  }

  const companyName = buyer.data?.company.name ?? "your company";
  const backHref = `/virtual-buyer/${buyerId}`;
  const a = analysis.data;

  const crumb = (
    <Link className="vb-detail-crumb" to={backHref}>
      <ChevronLeft size={13} aria-hidden="true" />
      {buyer.data?.name ?? "Buyer"}
    </Link>
  );

  // During buyer creation, the run is step 2 of the 3-step wizard (aits-2.0).
  // Re-runs from the detail page show the breadcrumb instead.
  const createSteps = (
    <div className="steps" aria-label="Create buyer progress">
      <span className="step done">
        <span className="num">
          <Check size={12} aria-hidden="true" />
        </span>
        Set up buyer &amp; questions
      </span>
      <span className="sep" />
      <span className="step active" aria-current="step" ref={scrollActiveIntoView}>
        <span className="num">2</span>
        Run simulation
      </span>
      <span className="sep" />
      <span className="step upcoming">
        <span className="num">3</span>
        Review verdict
      </span>
    </div>
  );
  const topNav = isCreateFlow ? createSteps : crumb;

  // ---- Terminal: DONE -> the effect above redirects to the verdict. Render a
  // brief placeholder for the one frame before navigation commits. ----
  if (a.status === "DONE") {
    return <VbLoading rows={4} />;
  }

  // ---- Terminal: FAILED ----
  if (a.status === "FAILED") {
    return (
      <div className="vb-run">
        {topNav}
        <div className="vb-run-wrap">
          <div className="vb-run-head">
            <h1 ref={headingRef} tabIndex={-1}>
              This run didn't finish
            </h1>
            <p>Something went wrong before the verdict was ready. You can run it again.</p>
          </div>
          <div className="vb-run-card vb-run-card--terminal">
            <div className="vb-run-failed">
              <div className="vb-run-failed__icon" aria-hidden="true">
                <AlertTriangle size={20} />
              </div>
              <p className="vb-run-failed__msg">
                {a.error || "The analysis failed unexpectedly. Please try again."}
              </p>
              {runError && (
                <div className="limit-flash show" role="alert">
                  {runError}
                </div>
              )}
              <div className="vb-run-failed__acts">
                <VbButton onClick={() => void runAgain()} disabled={triggerM.isPending}>
                  {triggerM.isPending ? "Starting…" : "Run again"}
                </VbButton>
                <VbButton variant="secondary" onClick={() => navigate(backHref)}>
                  Back to buyer
                </VbButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- In flight: PENDING / IN_PROGRESS ----
  const prog = parseRunProgress(a.progress);
  const qCount = questions.data?.length ?? 0;
  const modelCount = prog.legs.length || 3;
  const progLabel =
    qCount > 0
      ? `Asking questions · ${qCount} × ${modelCount} models`
      : `Asking the buyer's questions across ${modelCount} providers`;

  return (
    <div className="vb-run">
      {topNav}
      <div className="vb-run-wrap">
        <div className="vb-run-head">
          <h1>Asking AI about {companyName}…</h1>
          <p>This usually takes a minute or two. You can leave and come back.</p>
        </div>

        <div className="vb-run-card">
          <RunProviders legs={prog.legs} />

          <div>
            <div className="vb-run-prog-head">
              <span className="vb-run-prog-head__label">{progLabel}</span>
              <span className="pct" aria-hidden="true">
                {prog.overall}%
              </span>
            </div>
            <div
              className="vb-run-bar"
              role="progressbar"
              aria-label={`Analysis progress: ${prog.overall}%`}
              aria-valuenow={prog.overall}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${prog.overall}%` }} />
            </div>
          </div>

          <RunActivityLog key={analysisId} legs={prog.legs} questions={questions.data ?? []} />
        </div>

        <div className="vb-async">
          <div className="icon" aria-hidden="true">
            <Coffee size={17} />
          </div>
          <div className="body">
            <div className="t">You can leave this page</div>
            <div className="c">
              The run keeps going in the background. Come back anytime to check progress or view the
              verdict.
            </div>
          </div>
          <div className="acts">
            <VbButton variant="secondary" onClick={() => navigate(backHref)}>
              Run in background
              <ArrowRight size={14} aria-hidden="true" />
            </VbButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RunPage;
